'use client';
import React from 'react';
import { Formik, Form } from 'formik';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import * as Yup from 'yup';

import OrderDetail from '@views/OrderDetail';
import { OrderDetailProps } from '@views/OrderDetail/OrderDetailContent';
import Button from '@repo/ui/src/components/Button';
import { ErrorState, Input } from '@repo/ui/src/components/Form';
import LoadingLogo from '@components/Loading/LoadingLogo';
import { SearchOrderVariables, useGetOrder } from '@hooks/order/useGetOrder';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import { removeQueryParam } from '@utils/removeQueryParam';

const initialValues = {
  order_tracking_email: '',
  order_tracking_number: '',
};

interface OrderTrackingFormProps {
  orderDetails: Omit<OrderDetailProps, 'order'>;
}

const OrderTrackingForm = ({ orderDetails }: OrderTrackingFormProps) => {
  const { t } = useTranslation();
  const { trigger, data, isMutating } = useGetOrder();
  const { query, push } = useCurrentUrl();
  const queryValues = React.useMemo<SearchOrderVariables | false>(() => {
    // we can't use `router.query` because it is hydrated after mounting

    if (query.has('token')) {
      try {
        const { order, email } = JSON.parse(atob(query.get('token')!));
        return {
          order,
          email,
        };
      } catch {
        /* empty */
      }
    }
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validationSchema = Yup.object().shape({
    order_tracking_email: Yup.string()
      .email(
        t('orderTracking.emailInput.invalidText', undefined, {
          default: 'Please enter a valid email address',
        }),
      )
      .required(
        t('orderTracking.emailInput.requiredText', undefined, { default: 'Email is required' }),
      ),
    order_tracking_number: Yup.string().required(
      t('orderTracking.orderNumberInput.requireText', undefined, {
        default: 'Order number is required',
      }),
    ),
  });

  React.useEffect(() => {
    // Wait for order request to complete before removing token so the basket does not fetch
    if (query.get('token') && queryValues && !isMutating) {
      trigger(queryValues);
      removeQueryParam(query, 'token');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, isMutating]);

  if (queryValues && isMutating) {
    return <LoadingLogo className="sm:py-20 py-40" />;
  }

  if (data?.details.url) {
    push(data.details.url);
  }

  if (data && !data?.details.url) {
    return <OrderDetail {...orderDetails} order={data} />;
  }

  return (
    <div className="grid place-items-center w-full bg-white ">
      <section className="grid w-full max-w-2xl py-14 px-16 gap-y-8">
        <h1 className="tra-text-web-heading-3">
          {t('orderTracking.header', undefined, {
            default: 'View Order',
          })}
        </h1>
        <Formik
          initialValues={initialValues}
          validateOnChange={false}
          onSubmit={async (
            { order_tracking_email: email, order_tracking_number: order },
            { setStatus },
          ) => {
            try {
              await trigger({ email, order });
            } catch {
              setStatus(
                t('orderTracking.errorHelpText', undefined, {
                  default:
                    'Sorry, we are having trouble retrieving your order history. Please check back again later. If you need immediate assistance, feel free to reach out to our Customer Service team.',
                }),
              );
            }
          }}
          validationSchema={validationSchema}
        >
          {({ isSubmitting, status }) => (
            <Form className="grid gap-y-10">
              <div className="grid gap-y-5 sm:gap-y-7">
                <Input
                  type="email"
                  autoComplete="email"
                  name="order_tracking_email"
                  required
                  label={t('orderTracking.emailInput.label', undefined, {
                    default: 'Email Address',
                  })}
                />
                <Input
                  name="order_tracking_number"
                  required
                  label={t('orderTracking.orderNumberInput.label', undefined, {
                    default: 'Order Number',
                  })}
                />
              </div>

              {status && <ErrorState text={status} />}

              <Button size="large" type="submit" full disabled={isMutating}>
                {isSubmitting ? (
                  <LoadingLogo className="w-28 mx-auto" />
                ) : (
                  t('orderTracking.submitButton', undefined, {
                    default: 'View Order',
                  })
                )}
              </Button>
            </Form>
          )}
        </Formik>
      </section>
    </div>
  );
};

export default OrderTrackingForm;
